import { useMemo } from 'react';
import { Controller, useWatch, useFormState } from 'react-hook-form';
import { useGetCountryWithOperatorsListQuery } from '@src/services/country/countryWithOperatorsList/countryWithOperatorsListApiSlice';
import { Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { FormLabel, MultiSelectWithCheckboxes } from '@src/components/ui';
import { ICommonFormProps } from '@src/components/DynamicForm';
import { StyledError } from '@src/components/ui/Input/styles';
import { ETypographyIntents } from '@src/typings';
interface FormSelectOperatorProps extends ICommonFormProps {
    namePrefix?: string;
}

export const FormSelectOperator = (
    props: Readonly<FormSelectOperatorProps>
) => {
    const { t } = useTranslation();
    const { keyName, control, className, namePrefix, setValue } = props;
    const { touchedFields, isSubmitted } = useFormState({ control });
    const { data, isLoading, isFetching } = useGetCountryWithOperatorsListQuery(
        {}
    );
    const watchedCountry = useWatch({
        control,
        name: getName('globalCountry'),
    });
    const watchedOperators = useWatch({ control, name: getName('operators') });

    // Extract selected country ID from object
    const selectedCountryId = useMemo(() => {
        return typeof watchedCountry === 'object' && watchedCountry?.id
            ? watchedCountry.id
            : watchedCountry;
    }, [watchedCountry]);

    const countryOptions = data?.data.map((country) => ({
        label: country.name,
        value: country.id,
    }));

    const operatorOptions = useMemo(() => {
        return (
            data?.data
                ?.find((c) => c.id === selectedCountryId)
                ?.operators.map((op) => ({
                    label: `${op.name} (${op.mcc}-${op.mnc})`,
                    value: op.id,
                })) || []
        );
    }, [data, selectedCountryId]);

    function getName(fieldName: string) {
        if (namePrefix) return `${namePrefix}.${fieldName}`;
        return keyName ? `${keyName}.${fieldName}` : fieldName;
    }

    return (
        <div
            className={`grid sm:grid-flow-row sm:grid-cols-2 gap-4 items-baseline ${className}`}
        >
            <div className="w-full">
                <Controller
                    control={control}
                    name={getName('globalCountry')}
                    rules={{
                        required: {
                            value: true,
                            message: t('errors.formErrors.required', {
                                name: t('forms.country'),
                            }),
                        },
                    }}
                    render={({
                        field: { onChange, onBlur, value },
                        fieldState: { error: fieldStateError },
                    }) => {
                        // Use filtered errors prop if available for this field, otherwise use fieldState error
                        // But only show if field has been touched or form submitted
                        const fieldName = getName('globalCountry');
                        const error =
                            props.errors?.[fieldName] ?? fieldStateError;
                        const showError = Boolean(
                            error && (touchedFields[fieldName] || isSubmitted)
                        );
                        return (
                            <>
                                <FormLabel
                                    label={t('forms.country')}
                                    required
                                />
                                <Select
                                    size="large"
                                    placeholder="Select country"
                                    loading={isLoading || isFetching}
                                    onChange={(
                                        val: string | null | undefined
                                    ) => {
                                        const newValue = val ?? null;
                                        onChange(newValue);
                                        setValue?.(getName('operators'), null);
                                        onBlur();
                                    }}
                                    options={countryOptions}
                                    style={{ width: '100%' }}
                                    value={value?.id ?? value}
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.label ?? '')
                                            .toLowerCase()
                                            .includes(input.toLowerCase())
                                    }
                                />

                                {showError && error?.message && (
                                    <StyledError
                                        intent={ETypographyIntents.Badge}
                                    >
                                        {error.message}
                                    </StyledError>
                                )}
                            </>
                        );
                    }}
                />
            </div>
            <div className="w-full">
                <Controller
                    control={control}
                    name={getName('operators')}
                    rules={{
                        required: {
                            value: true,
                            message: t('errors.formErrors.required', {
                                name: t('forms.operators'),
                            }),
                        },
                    }}
                    render={({
                        field: { onChange, onBlur, value },
                        fieldState: { error: fieldStateError },
                    }) => {
                        // Use filtered errors prop if available for this field, otherwise use fieldState error
                        // But only show if field has been touched or form submitted
                        const fieldName = getName('operators');
                        const error =
                            props.errors?.[fieldName] ?? fieldStateError;
                        const showError = Boolean(
                            error && (touchedFields[fieldName] || isSubmitted)
                        );
                        return (
                            <>
                                <FormLabel
                                    label={t('forms.operators')}
                                    required
                                />
                                <MultiSelectWithCheckboxes
                                    size="large"
                                    placeholder="Select operator"
                                    value={value}
                                    onChange={(
                                        val:
                                            | {
                                                  value: string;
                                                  label: string;
                                              }[]
                                            | null
                                            | undefined
                                    ) => {
                                        onChange(val ?? []);
                                        onBlur();
                                    }}
                                    disabled={!selectedCountryId}
                                    options={operatorOptions}
                                    fullWidth
                                />
                                {showError && error?.message && (
                                    <StyledError
                                        intent={ETypographyIntents.Badge}
                                    >
                                        {error.message}
                                    </StyledError>
                                )}
                            </>
                        );
                    }}
                />
            </div>
        </div>
    );
};
